'use client';

import React, { FC } from 'react';

import Slider from 'components/commercetools-ui/atoms/slider';
import Typography from 'components/commercetools-ui/atoms/typography';
import { desktop, minitablet, mobile, smallMobile, tablet } from 'helpers/utils/screensizes';
import useMediaQuery from 'helpers/hooks/useMediaQuery';
import { useParams, usePathname } from 'next/navigation';
import ProductTile from '../product-tile';
import { mapProduct } from 'helpers/algolia/map-product';
import { isPdpUrl } from 'helpers/utils/pdpUrlHelper';

export interface ProductSliderYoulikeProps {
  /** Server-fetched Algolia hits (backend action → Redis). See the you-might-also-like tastic. */
  hits: any[];
}

/**
 * "You might also like". A pure view: its products are fetched on the SERVER (see
 * frontastic/tastics/products/product-list-you-like), so nothing here queries Algolia and the tiles
 * are in the SSR HTML.
 */
const ProductSliderAlgoliaYouLike: FC<ProductSliderYoulikeProps> = ({ hits }) => {
  const [isMiniTabletSize] = useMediaQuery(minitablet);
  const [isTabletSize] = useMediaQuery(tablet);
  const [isDesktopSize] = useMediaQuery(desktop);

  const currentURL = usePathname();
  const { locale } = useParams();
  const isProductDetailPage = isPdpUrl(currentURL);

  return (
    <div
      id="related-products"
      className={`py-32 pl-24 md:pb-92 md:pt-48 lg:py-48 lg:px-48 xl:px-60 2xl:px-80 xl:py-100  ${
        isProductDetailPage
          ? 'relative left-full w-screen -translate-x-full bg-[#F3EDE9] px-48 md:pb-1 md:pt-100 lg:grid lg:grid-cols-12 lg:pl-0 xl:pb-153 xl:pt-153'
          : 'bg-[#E5E9EB] md:px-48'
      }`}
    >
      <div className={isProductDetailPage ? 'lg:col-span-9 lg:col-start-4' : ''}>
        <div>
          <Typography
            id="related-products-title"
            className="pb-32 text-32 font-medium text-primary-black xl:text-36 2xl:text-48"
            as="h3"
          >
            You might also like
          </Typography>
        </div>
        <div>
          <div className="product-slider-container relative w-full">
            <Slider
              className="mb-48"
              slidesPerView={2.1}
              slidesPerGroup={1}
              dots={false}
              autoplay={false}
              arrows={isTabletSize || isDesktopSize || isMiniTabletSize}
              innerArrows
              solidArrows
              allowArrowsOnTouchDevice
              nextButtonStyles={{ top: '110%', left: 50, padding: '10px' }}
              prevButtonStyles={{ top: '110%', left: 0, padding: '10px' }}
              allowTouchMove
              spaceBetween={8}
              breakpoints={{
                [tablet]: {
                  slidesPerView: isProductDetailPage ? 3.25 : 4,
                  slidesPerGroup: 4,
                  spaceBetween: 16,
                },
                [smallMobile]: { slidesPerView: 1.2, slidesPerGroup: 1, spaceBetween: 16 },
                [mobile]: { slidesPerView: 2.1, slidesPerGroup: 2, spaceBetween: 16 },
              }}
            >
              {hits.map((hit: any, index: number) => (
                <div key={hit.objectID ?? hit.key ?? index} className="mb-48">
                  <ProductTile product={mapProduct(hit, locale as string)} layout="grid" />
                </div>
              ))}
            </Slider>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductSliderAlgoliaYouLike;
